import React from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';

export default function NotificationComposer({ onClose }) {
  const { role, createNotification } = useAuth();
  const allowed = role === 'Majitel' || role === 'Admin';

  const [text, setText] = React.useState('');
  const [search, setSearch] = React.useState('');
  const [results, setResults] = React.useState([]);
  const [showResults, setShowResults] = React.useState(false);
  const [selected, setSelected] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [message, setMessage] = React.useState('');
  const inputRef = React.useRef(null);
  const [url, setUrl] = React.useState('');
  const [customThumb, setCustomThumb] = React.useState('');

  React.useEffect(() => {
    // Force focus into the text input as soon as the modal opens
    setTimeout(() => inputRef.current?.focus(), 0);
    try {
      document.documentElement.classList.add('modal-open');
    } catch {}
    return () => { try { document.documentElement.classList.remove('modal-open'); } catch {} };
  }, []);

  React.useEffect(() => {
    let alive = true;
    const run = async () => {
      if (!search || search.length < 2) { if (alive) setResults([]); return; }
      try {
        const r = await fetch('/api/popups?_=' + Date.now(), { cache: 'no-store' });
        if (!r.ok) return;
        const arr = await r.json();
        const q = search.toLowerCase();
        const filtered = (Array.isArray(arr) ? arr : []).filter(p => (p.title||'').toLowerCase().includes(q));
        if (!alive) return;
        setResults(filtered.slice(0, 30));
        setShowResults(true);
      } catch {}
    };
    run();
    return () => { alive = false; };
  }, [search]);

  const suggestUrlForPopup = (p) => {
    try {
      const id = String(p.id);
      const kind = String(p.contentType || '').toLowerCase();
      if (kind === 'serial') return `/serialy?preview=${encodeURIComponent(id)}`;
      if (kind === 'film') return `/filmy?preview=${encodeURIComponent(id)}`;
      return `/?preview=${encodeURIComponent(id)}`;
    } catch { return ''; }
  };

  const selectPopup = (p) => {
    // Strictly prefer tile/thumbnail (listThumb). Do NOT fall back to poster/hero.
    const tile = p.listThumb || p.listThumbnail || p.tile || p.thumb || '';
    setSelected({ id: String(p.id), title: p.title || '', thumb: tile, contentType: p.contentType || '' });
    // keep focus on the text box for quick typing
    setTimeout(() => inputRef.current?.focus(), 0);
    // hide results until user clicks the search box again
    setShowResults(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!allowed) { setMessage('Nemáš oprávnění.'); return; }
    if (!text.trim() && !selected) { setMessage('Zadej text oznámení nebo vyber popup.'); return; }
    setBusy(true);
    setMessage('');
    try {
      const thumb = (customThumb || selected?.thumb || '').trim();
      await createNotification({ text, popupId: selected?.id, popupTitle: selected?.title, popupThumb: thumb, url });
      setText('');
      setSelected(null);
      setSearch('');
      setResults([]);
  setUrl('');
      setCustomThumb('');
      setMessage('Notifikace odeslána.');
      // keep the composer open for další notifikaci; nebo zavřít: onClose?.()
    } catch (e) {
      setMessage('Chyba: ' + (e?.message || 'unknown'));
    } finally {
      setBusy(false);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  const close = () => { onClose && onClose(); };

  const content = (
    <Overlay onClick={close}>
      <Modal role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>Vytvořit notifikaci</Title>
          <Close onClick={close} aria-label="Zavřít">✕</Close>
        </Header>
        {!allowed ? (
          <Info>Pouze Admin/Majitel mohou vytvářet notifikace.</Info>
        ) : (
          <Form onSubmit={submit}>
            <Row>
              <Left>
                {(customThumb || selected?.thumb) ? (<Thumb src={customThumb || selected?.thumb} alt="thumb" />) : (<ThumbPlaceholder />)}
              </Left>
              <Right>
                <Label htmlFor="notif-text" onClick={() => inputRef.current?.focus()}>Text oznámení</Label>
                <Textarea
                  id="notif-text"
                  ref={inputRef}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onMouseDown={(e) => { e.stopPropagation(); }}
                  onClick={(e) => { e.stopPropagation(); e.currentTarget.focus(); }}
                  placeholder="Např. Nová epizoda je venku!"
                />

                <Label style={{marginTop:10}}>Vybrat seriál/film (volitelné)</Label>
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onMouseDown={(e) => { e.stopPropagation(); }}
                  onClick={(e) => { e.stopPropagation(); e.currentTarget.focus(); if ((search||'').length>=2) setShowResults(true); }}
                  placeholder="Hledat podle názvu..."
                />
                {showResults && results.length > 0 && (
                  <Results onMouseDown={(e) => e.stopPropagation()}>
                    {results.map(p => {
                      const tile = p.listThumb || p.listThumbnail || p.tile || p.thumb || '';
                      return (
                        <ResultItem key={p.id} type="button" onClick={() => selectPopup(p)}>
                          <Mini src={tile || '/images/placeholder-thumb.png'} alt="mini" />
                          <span>{p.title}</span>
                        </ResultItem>
                      );
                    })}
                  </Results>
                )}

                <Label style={{marginTop:10}}>Cílová URL (volitelné, skrytá v UI)</Label>
                <Input
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  onMouseDown={(e) => { e.stopPropagation(); }}
                  onClick={(e) => { e.stopPropagation(); e.currentTarget.focus(); }}
                  placeholder="/serialy?preview=... nebo /play/12345 nebo https://…"
                />
                {selected?.id && (
                  <Row style={{gridTemplateColumns:'1fr'}}>
                    <Btn type="button" onClick={() => setUrl(suggestUrlForPopup(selected))}>Použít URL z popupu</Btn>
                  </Row>
                )}

                <Label style={{marginTop:10}}>Vlastní URL obrázku (dlaždice)</Label>
                <Input
                  value={customThumb}
                  onChange={(e) => setCustomThumb(e.target.value)}
                  onMouseDown={(e) => { e.stopPropagation(); }}
                  onClick={(e) => { e.stopPropagation(); e.currentTarget.focus(); }}
                  placeholder="https://… nebo /images/... (použije se místo dlaždice z popupu)"
                />
              </Right>
            </Row>
            {message && <Info>{message}</Info>}
            <Actions>
              <Btn type="button" onClick={close}>Zrušit</Btn>
              <Primary type="submit" disabled={busy}>{busy ? 'Odesílám…' : 'Odeslat'}</Primary>
            </Actions>
          </Form>
        )}
      </Modal>
    </Overlay>
  );

  // Render in a portal to escape any parent stacking/transform contexts
  const target = typeof document !== 'undefined' ? document.body : null;
  return target ? ReactDOM.createPortal(content, target) : content;
}

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(10,10,12,0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100000;
`;

const Modal = styled.div`
  width: min(900px, 96vw);
  background: linear-gradient(180deg,#0f1720,#11131a);
  border-radius: 12px;
  box-shadow: 0 20px 50px rgba(2,6,23,0.6);
  padding: 18px;
  color: #e6eef8;
  border: 1px solid rgba(255,255,255,0.06);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: 700;
`;

const Close = styled.button`
  appearance: none;
  border: 0;
  background: transparent;
  color: #e6eef8;
  font-size: 18px;
  cursor: pointer;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 220px 1fr;
  gap: 12px;
  align-items: start;
  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const Left = styled.div``;
const Right = styled.div`display:flex; flex-direction:column;`;

const Thumb = styled.img`
  width: 100%;
  height: auto;
  border-radius: 10px;
  object-fit: cover;
`;
const ThumbPlaceholder = styled.div`
  width: 100%;
  padding-top: 56.25%;
  border-radius: 10px;
  background: rgba(255,255,255,0.06);
`;

const Label = styled.label`
  font-size: 12px;
  opacity: 0.85;
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 90px;
  padding: 10px;
  border-radius: 10px;
  border: 1px solid rgba(255,255,255,0.06);
  background: rgba(255,255,255,0.02);
  color: #e6eef8;
  outline: none;
  font-size: 14px;
  resize: vertical;
`;

const Input = styled.input`
  width: 100%;
  height: 40px;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid rgba(255,255,255,0.06);
  background: rgba(255,255,255,0.02);
  color: #e6eef8;
  outline: none;
  font-size: 14px;
`;

const Results = styled.div`
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 8px;
  padding: 6px;
  max-height: 240px;
  overflow: auto;
  display: grid;
  grid-template-columns: 1fr;
  gap: 6px;
  margin-top: 6px;
`;

const ResultItem = styled.button`
  width: 100%;
  text-align: left;
  appearance: none;
  border: 0;
  background: transparent;
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 6px;
  border-radius: 6px;
  cursor: pointer;
  color: #e6eef8;
  &:hover { background: rgba(255,255,255,0.04); }
`;

const Mini = styled.img`
  width: 56px;
  height: 32px;
  object-fit: cover;
  border-radius: 6px;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const Btn = styled.button`
  appearance: none;
  border: 1px solid rgba(255,255,255,0.12);
  background: transparent;
  color: #e6eef8;
  border-radius: 8px;
  padding: 8px 12px;
  cursor: pointer;
`;

const Primary = styled.button`
  appearance: none;
  border: 0;
  border-radius: 8px;
  background: linear-gradient(90deg,#1e90ff,#007bff);
  color: #fff;
  font-weight: 700;
  padding: 10px 14px;
  cursor: pointer;
`;

const Info = styled.div`
  color: #cfdff6;
`;
